import { useMemo } from "react";
import { Button, Card, Row, Table } from "react-bootstrap";
import { Table as RBITable } from "react-bootstrap-icons";
import { Bar } from "react-chartjs-2";
import { useParams } from "react-router-dom";
import useData from "../../../Hooks/useData";

import { InvoicedByDeptDelta } from "../../../Types/Reports";
import { formatNumber } from "../../../Utils/Format";
import { saveWorkbook, useChartColours } from "./Utils";
import ExcelJS from "exceljs";

const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
    },
};

function applyNumeric(row: ExcelJS.Row, value: number, key: string) {
    row.getCell(key).value = value;
    row.getCell(key).numFmt =
        '_-[$£-en-GB]* #,##0.00_-;-[$£-en-GB]* #,##0.00_-;_-[$£-en-GB]* "-"??_-;_-@_-';
}

function applyNumericTotal(cell: ExcelJS.Cell, value: number, column: string, dataLength: number) {
    cell.style = { font: { bold: true } };
    cell.value = {
        formula: `=SUM(${column}2:${column}${dataLength + 1})`,
        result: value,
        sharedFormula: `=SUM(${column}2:${column}${dataLength + 1})`,
        date1904: false,
    };
    cell.numFmt =
        '_-[$£-en-GB]* #,##0.00_-;-[$£-en-GB]* #,##0.00_-;_-[$£-en-GB]* "-"??_-;_-@_-';
    cell.border = {
        top: { style: "thick" },
    };
}

function generateSpreadsheet(data: InvoicedByDeptDelta[], runID: string) {
    const workbook = new ExcelJS.Workbook();
    workbook.calcProperties.fullCalcOnLoad = true;

    const sheet = workbook.addWorksheet("Invoiced Per Dept Delta", {
        views: [{ state: "frozen", ySplit: 1 }],
    });

    sheet.columns = [
        { header: "ID", key: "deptId", width: 4 },
        { header: "Department Name", key: "deptName", width: 40 },
        { header: "Net Total", key: "invoiced", width: 15 },
        { header: "Total Prev Run", key: "invoicedLastRun", width: 15 },
        { header: "Delta", key: "delta", width: 15 },
    ];

    const header = sheet.getRow(1);
    let index = 0;
    for (const col of ["ID", "Department Name", "Net Total", "Total Prev Run", "Delta"]) {
        index++;
        header.getCell(index).value = col;
        header.getCell(index).style = { font: { bold: true } };
        header.getCell(index).border = { bottom: { style: "thick" } };
    }

    for (let item = 0; item < data.length; item++) {
        const row = sheet.getRow(item + 2);
        row.getCell("deptId").value = data[item].deptId;
        row.getCell("deptName").value = data[item].deptName;

        applyNumeric(row, data[item].invoiced, "invoiced");
        applyNumeric(row, data[item].invoicedLastRun, "invoicedLastRun");
        applyNumeric(row, data[item].delta, "delta");
    }

    applyNumericTotal(sheet.getRow(data.length + 2).getCell(3), data.reduce((a, b) => a + b.invoiced, 0), "C", data.length);
    applyNumericTotal(sheet.getRow(data.length + 2).getCell(4), data.reduce((a, b) => a + b.invoicedLastRun, 0), "D", data.length);
    applyNumericTotal(sheet.getRow(data.length + 2).getCell(5), data.reduce((a, b) => a + b.delta, 0), "E", data.length);

    saveWorkbook(`Invoice Run ${runID} - Invoiced Per Dept Delta.xlsx`, workbook);
}

export default function InvoicedPerDeptDelta() {
    const { run: runID } = useParams<{ run: string }>();
    const invoicedByDeptDelta = useData<InvoicedByDeptDelta[]>("/API/Reports/Runs/{run}/InvoicedPerDeptDelta", {
        run: runID,
    });
    const chartColours = useChartColours(invoicedByDeptDelta.data?.length ?? 0);
    const data = useMemo(() => {
        if (invoicedByDeptDelta.data) {
            return {
                labels: invoicedByDeptDelta.data.map(i => `${i.deptName ?? "Unassigned"} (£)`),
                datasets: [
                    {
                        label: "Delta",
                        data: invoicedByDeptDelta.data.map(i => i.delta),
                        ...chartColours,
                        borderWidth: 1,
                    },
                ],
            };
        }
        return null;
    }, [invoicedByDeptDelta.dataUpdatedAt, chartColours]);

    if (!invoicedByDeptDelta.data) {
        return <h6>Loading...</h6>;
    }
    var nullAny: any = null;
    return (
        <div>
            <div className="d-flex flex-row align-items-center pt-2 pb-2">
                <h3 className="flex-grow-1">Invoiced Per Dept (compared to previous finished run)</h3>
                <Button
                    onClick={() => {
                        generateSpreadsheet(invoicedByDeptDelta.data ?? [], runID);
                    }}
                    variant="success">
                    Export to Excel <RBITable />
                </Button>
            </div>
            <Card className="mb-2">
                <Bar type="bar" data={data} height={500} width={nullAny} options={chartOptions} />
            </Card>
            <Table size="sm" bordered>
                <thead>
                    <tr className="bg-secondary">
                        <th>ID</th>
                        <th>Department Name</th>
                        <th>Net Total</th>
                        <th>Total Prev Run</th>
                        <th>Delta</th>
                    </tr>
                </thead>
                <tbody>
                    {invoicedByDeptDelta.data.map(i => (
                        <tr key={i.deptId}>
                            <td>{i.deptId ?? "N/A"}</td>
                            <td className="font-weight-bold">{i.deptName ?? "Unassigned"}</td>
                            <td className="text-right text-monospace">
                                {formatNumber(i.invoiced, "GBP")}
                            </td>
                            <td className="text-right text-monospace">
                                {formatNumber(i.invoicedLastRun, "GBP")}
                            </td>
                            <td className="text-right text-monospace">
                                {formatNumber(i.delta, "GBP")}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}
